import type { CSSProperties } from 'react';
import { AttachmentCardUI } from './AttachmentCardUI';
import type { AttachmentCardKind } from './AttachmentCardUI';
import styles from './AttachmentCardsGridUI.module.css';

export interface AttachmentCardItem {
  id: string;
  kind: AttachmentCardKind;
  /** image */
  src?: string;
  alt?: string;
  /** file */
  fileType?: string;
  name?: string;
  /** Opt a card out of the grid's click handler when it has nothing to open. Default: clickable. */
  interactive?: boolean;
}

/** "research-summary.pdf" → { type: 'PDF', name: 'research-summary' } for the file-card layout. */
export const splitFilename = (filename: string): { type: string; name: string } => {
  const dot = filename.lastIndexOf('.');
  if (dot <= 0) return { type: 'FILE', name: filename };
  return { type: filename.slice(dot + 1).toUpperCase(), name: filename.slice(0, dot) };
};

export type AttachmentCardsLayout = 'grid' | 'row';

export interface AttachmentCardsGridUIProps {
  items: AttachmentCardItem[];
  onItemClick?: (id: string) => void;
  /** When provided, each card shows a remove control (input-bar / pre-send state). */
  onRemove?: (id: string) => void;
  /**
   * 'grid' (default) — balanced rows sized to the item count, for the sent message.
   * 'row' — a single non-wrapping line that scrolls horizontally, for the input bars.
   */
  layout?: AttachmentCardsLayout;
}

/**
 * Balanced column count for the sent-message grid: 1–3 sit on one row; 4 splits 2+2 rather than
 * orphaning a single card on a second row; 5 and up cap at 3 per row (5 → 3+2, 6 → 3+3, …).
 */
const columnsFor = (count: number): number => (count <= 3 ? Math.max(count, 1) : count === 4 ? 2 : 3);

/**
 * Lays out attachment cards — a balanced grid above the user message (post-send), or a single
 * horizontally-scrolling line inside the input bar (pre-send).
 */
export const AttachmentCardsGridUI = ({
  items,
  onItemClick,
  onRemove,
  layout = 'grid',
}: AttachmentCardsGridUIProps) => {
  if (items.length === 0) return null;

  return (
    <div
      className={styles.grid}
      data-layout={layout}
      style={{ '--attachment-columns': columnsFor(items.length) } as CSSProperties}
    >
      {items.map((item) => (
        <AttachmentCardUI
          key={item.id}
          kind={item.kind}
          src={item.src}
          alt={item.alt}
          fileType={item.fileType}
          name={item.name}
          onClick={onItemClick && item.interactive !== false ? () => onItemClick(item.id) : undefined}
          onRemove={onRemove ? () => onRemove(item.id) : undefined}
        />
      ))}
    </div>
  );
};
